import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Images from "../../../utils/Images";
import { useState } from "react";
import { ArrowRight, ShieldCheck, Users as UsersIcon, Star } from "lucide-react";

export default function ChooseRole() {
  const [selectedRole, setSelectedRole] = useState("driver");
  const handleSelectedRole = (role) => {
    setSelectedRole(role);
  };
  const navigate = useNavigate();

  const cards = [
    {
      title: "Drivers & Riders",
      description: "Taxi drivers, Okada & dispatch riders",
      to: "/signup/riders",
      Icon: Images.driver,
      key: "driver",
    },
    {
      title: "General Agent",
      description: "Broker/agency managing multiple clients (coming soon)",
      to: "/signup/general-agent",
      Icon: Images.general_agent,
      key: "general-agent",
    },
    {
      title: "Direct Agent",
      description: "Individual agent/affiliate (coming soon)",
      to: "/signup/direct-agent",
      Icon: Images.direct_agent,
      key: "direct-agent",
    },
  ];

  return (
    <div className="grid grid-cols-1 min-h-screen lg:grid-cols-2 lg:h-screen lg:overflow-hidden">
      <motion.div
        className="hidden relative w-full h-full lg:block"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <img
          src={`https://images.unsplash.com/photo-1560264418-c4445382edbc?q=80&w=1470&auto=format&fit=crop`}
          alt="trip-secure-choose-role"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30" />
        <div className="flex absolute inset-0 flex-col justify-between p-12">
          <div>
            <img src={Images.logo_navbar} alt="TripSecure" className="h-10" />
          </div>
          <div className="max-w-xl">
            <div className="p-6 rounded-2xl border border-white/10 bg-white/10 backdrop-blur-md shadow-[0_8px_30px_rgba(0,0,0,0.2)]">
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-2 text-2xl font-extrabold text-white"
              >
                Grow with TripSecure Agents
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
                className="mb-4 text-sm text-white/80"
              >
                Earn commissions, onboard drivers and riders, and manage your portfolio—all in one dashboard.
              </motion.p>
              <div className="grid grid-cols-3 gap-3">
                <div className="flex gap-2 items-center p-3 rounded-xl border border-white/10 bg-white/10">
                  <UsersIcon size={16} className="text-emerald-300" />
                  <span className="text-xs text-white/90">1k+ agents</span>
                </div>
                <div className="flex gap-2 items-center p-3 rounded-xl border border-white/10 bg-white/10">
                  <ShieldCheck size={16} className="text-emerald-300" />
                  <span className="text-xs text-white/90">Secure payouts</span>
                </div>
                <div className="flex gap-2 items-center p-3 rounded-xl border border-white/10 bg-white/10">
                  <Star size={16} className="text-emerald-300" />
                  <span className="text-xs text-white/90">Trusted by fleets</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="flex justify-center items-start p-6 h-auto lg:p-12 lg:h-screen lg:overflow-y-auto">
        <div className="w-full max-w-xl">
          <div className="flex justify-center mb-6">
            <img src={Images.logo_colored} alt="TripSecure" className="h-16" />
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-2 text-2xl font-bold text-center"
          >
            Create Account
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-6 text-sm text-center text-gray-500"
          >
            Choose the role that best describes you. We will tailor the signup to match the information required.
          </motion.p>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {cards.map(({ title, to, Icon, key }) => (
              <motion.button
                type="button"
                onClick={() => handleSelectedRole(key)}
                key={to}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                whileHover={{ y: -2 }}
                className={`overflow-hidden rounded-xl border bg-white/95 p-5 text-left shadow-sm focus:outline-none ${
                  selectedRole === key
                    ? "border-emerald-500 ring-2 ring-emerald-100"
                    : "border-gray-200"
                }`}
              >
                <div className="flex flex-col gap-3 items-center">
                  <div className="flex justify-center items-center w-16 h-16 bg-emerald-50 rounded-full">
                    <img src={Icon} alt={title} className="object-cover w-16 h-16" />
                  </div>
                  <h3 className={`text-sm font-semibold text-center ${selectedRole === key ? "text-emerald-600" : "text-gray-700"}`}>{title}</h3>
                </div>
              </motion.button>
            ))}
          </div>

          <motion.button
            type="button"
            onClick={() => {
              const routes = {
                'driver': '/signup/riders',
                'general-agent': '/signup/general-agent',
                'direct-agent': '/signup/direct-agent',
              };
              navigate(routes[selectedRole]);
            }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4 }}
            className="overflow-hidden relative px-5 py-3 mt-8 w-full text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl shadow-lg hover:from-emerald-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:ring-offset-2"
          >
            <span className="flex relative z-10 gap-2 justify-center items-center">
              Continue
              <ArrowRight size={16} className="opacity-90" />
            </span>
            <span className="absolute inset-0 bg-gradient-to-r transition-transform duration-700 -translate-x-full pointer-events-none from-white/0 via-white/20 to-white/0" />
          </motion.button>
        </div>
      </div>
    </div>
  );
}
